import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getMessages = createAsyncThunk(
    'inbox/getMessages',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.INBOX, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Messages retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch messages',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch messages';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch messages';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMessage = createAsyncThunk(
    'inbox/getMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.INBOX_MESSAGE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message retrieved successfully',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createMessage = createAsyncThunk(
    'inbox/createMessage',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX, data);
            const { status, message, data: responseData } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Message created successfully',
                    message: responseData?.message || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to create message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create message';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateMessage = createAsyncThunk(
    'inbox/updateMessage',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.INBOX_MESSAGE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message updated successfully',
                    message: responseData?.message || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update message';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deleteMessage = createAsyncThunk(
    'inbox/deleteMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.INBOX_MESSAGE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkDeleteMessages = createAsyncThunk(
    'inbox/bulkDelete',
    async (ids, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_BULK_DELETE, { ids });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Messages deleted successfully',
                    deleted_count: data?.deleted_count || 0
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete messages',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete messages';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete messages';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const markAsRead = createAsyncThunk(
    'inbox/markAsRead',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_READ(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message marked as read',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to mark message as read',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to mark message as read';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to mark message as read';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const markAsUnread = createAsyncThunk(
    'inbox/markAsUnread',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_UNREAD(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message marked as unread',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to mark message as unread',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to mark message as unread';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to mark message as unread';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const flagMessage = createAsyncThunk(
    'inbox/flagMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_FLAG(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message flagged successfully',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to flag message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to flag message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to flag message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const unflagMessage = createAsyncThunk(
    'inbox/unflagMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_UNFLAG(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message unflagged successfully',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to unflag message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to unflag message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to unflag message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const escalateMessage = createAsyncThunk(
    'inbox/escalateMessage',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_ESCALATE(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Message escalated successfully',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to escalate message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to escalate message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to escalate message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const replyMessage = createAsyncThunk(
    'inbox/replyMessage',
    async ({ id, reply_message }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.INBOX_REPLY(id), { reply_message });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Reply sent successfully',
                    message: data?.message || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send reply',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send reply';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send reply';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getInboxStats = createAsyncThunk(
    'inbox/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.INBOX_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Inbox statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch inbox statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch inbox statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch inbox statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMemberMessages = createAsyncThunk(
    'inbox/getMemberMessages',
    async ({ memberId, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.INBOX_MEMBER_MESSAGES(memberId), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member messages retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member messages',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch member messages';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member messages';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportInbox = createAsyncThunk(
    'inbox/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.INBOX_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    messages: [],
    currentMessage: null,
    memberMessages: [],
    stats: {
        total_messages: 0,
        unread_messages: 0,
        read_messages: 0,
        flagged_messages: 0,
        escalated_messages: 0,
        messages_by_day: {},
        top_senders: [],
        stats: {
            total: 0,
            unread: 0,
            read: 0,
            flagged: 0,
            escalated: 0,
        }
    },
    exportData: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const inboxSlice = createSlice({
    name: 'inbox',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearInboxState: (state) => {
            state.messages = [];
            state.currentMessage = null;
            state.memberMessages = [];
            state.stats = initialState.stats;
            state.exportData = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentMessage: (state) => {
            state.currentMessage = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
        clearMemberMessages: (state) => {
            state.memberMessages = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET MESSAGES =====
            .addCase(getMessages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.messages = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Messages retrieved successfully';
                state.apiMessage = payload.message || 'Messages retrieved successfully';
                state.error = null;
            })
            .addCase(getMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch messages';
                state.apiMessage = action.payload?.message || 'Failed to fetch messages';
                state.successMessage = null;
                state.messages = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET MESSAGE =====
            .addCase(getMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentMessage = payload.message || null;
                state.successMessage = payload.message || 'Message retrieved successfully';
                state.apiMessage = payload.message || 'Message retrieved successfully';
                state.error = null;
            })
            .addCase(getMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch message';
                state.apiMessage = action.payload?.message || 'Failed to fetch message';
                state.successMessage = null;
                state.currentMessage = null;
            })

            // ===== CREATE MESSAGE =====
            .addCase(createMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message created successfully';
                state.apiMessage = payload.message || 'Message created successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = [payload.message, ...state.messages];
                }
            })
            .addCase(createMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create message';
                state.apiMessage = action.payload?.message || 'Failed to create message';
                state.successMessage = null;
            })

            // ===== UPDATE MESSAGE =====
            .addCase(updateMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message updated successfully';
                state.apiMessage = payload.message || 'Message updated successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(updateMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update message';
                state.apiMessage = action.payload?.message || 'Failed to update message';
                state.successMessage = null;
            })

            // ===== DELETE MESSAGE =====
            .addCase(deleteMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message deleted successfully';
                state.apiMessage = payload.message || 'Message deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.messages = state.messages.filter(m => m.id !== payload.id);
                    if (state.currentMessage?.id === payload.id) {
                        state.currentMessage = null;
                    }
                }
            })
            .addCase(deleteMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete message';
                state.apiMessage = action.payload?.message || 'Failed to delete message';
                state.successMessage = null;
            })

            // ===== BULK DELETE =====
            .addCase(bulkDeleteMessages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkDeleteMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Messages deleted successfully';
                state.apiMessage = payload.message || 'Messages deleted successfully';
                state.error = null;
                // Refresh messages after bulk delete
                state.messages = [];
            })
            .addCase(bulkDeleteMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete messages';
                state.apiMessage = action.payload?.message || 'Failed to delete messages';
                state.successMessage = null;
            })

            // ===== MARK AS READ =====
            .addCase(markAsRead.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(markAsRead.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message marked as read';
                state.apiMessage = payload.message || 'Message marked as read';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(markAsRead.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to mark message as read';
                state.apiMessage = action.payload?.message || 'Failed to mark message as read';
                state.successMessage = null;
            })

            // ===== MARK AS UNREAD =====
            .addCase(markAsUnread.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(markAsUnread.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message marked as unread';
                state.apiMessage = payload.message || 'Message marked as unread';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(markAsUnread.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to mark message as unread';
                state.apiMessage = action.payload?.message || 'Failed to mark message as unread';
                state.successMessage = null;
            })

            // ===== FLAG MESSAGE =====
            .addCase(flagMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(flagMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message flagged successfully';
                state.apiMessage = payload.message || 'Message flagged successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(flagMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to flag message';
                state.apiMessage = action.payload?.message || 'Failed to flag message';
                state.successMessage = null;
            })

            // ===== UNFLAG MESSAGE =====
            .addCase(unflagMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(unflagMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message unflagged successfully';
                state.apiMessage = payload.message || 'Message unflagged successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(unflagMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to unflag message';
                state.apiMessage = action.payload?.message || 'Failed to unflag message';
                state.successMessage = null;
            })

            // ===== ESCALATE MESSAGE =====
            .addCase(escalateMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(escalateMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Message escalated successfully';
                state.apiMessage = payload.message || 'Message escalated successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(escalateMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to escalate message';
                state.apiMessage = action.payload?.message || 'Failed to escalate message';
                state.successMessage = null;
            })

            // ===== REPLY MESSAGE =====
            .addCase(replyMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(replyMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Reply sent successfully';
                state.apiMessage = payload.message || 'Reply sent successfully';
                state.error = null;
                if (payload.message) {
                    state.messages = state.messages.map(m => 
                        m.id === payload.message.id ? payload.message : m
                    );
                    if (state.currentMessage?.id === payload.message.id) {
                        state.currentMessage = payload.message;
                    }
                }
            })
            .addCase(replyMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send reply';
                state.apiMessage = action.payload?.message || 'Failed to send reply';
                state.successMessage = null;
            })

            // ===== GET STATS =====
            .addCase(getInboxStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getInboxStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Inbox statistics retrieved successfully';
                state.apiMessage = payload.message || 'Inbox statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getInboxStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch inbox statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch inbox statistics';
                state.successMessage = null;
            })

            // ===== GET MEMBER MESSAGES =====
            .addCase(getMemberMessages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.memberMessages = responseData.data || [];
                state.successMessage = payload.message || 'Member messages retrieved successfully';
                state.apiMessage = payload.message || 'Member messages retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member messages';
                state.apiMessage = action.payload?.message || 'Failed to fetch member messages';
                state.successMessage = null;
                state.memberMessages = [];
            })

            // ===== EXPORT =====
            .addCase(exportInbox.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportInbox.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportInbox.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearInboxState,
    clearCurrentMessage,
    clearExportData,
    clearMemberMessages,
} = inboxSlice.actions;

export const selectMessages = (state) => state.inbox?.messages || [];
export const selectCurrentMessage = (state) => state.inbox?.currentMessage || null;
export const selectMemberMessages = (state) => state.inbox?.memberMessages || [];
export const selectInboxStats = (state) => state.inbox?.stats || initialState.stats;
export const selectInboxPagination = (state) => state.inbox?.pagination || initialState.pagination;
export const selectInboxLoading = (state) => state.inbox?.isLoading || false;
export const selectInboxError = (state) => state.inbox?.error || null;
export const selectInboxSuccess = (state) => state.inbox?.successMessage || null;
export const selectInboxApiMessage = (state) => state.inbox?.apiMessage || null;
export const selectInboxExportData = (state) => state.inbox?.exportData || null;

export const selectInboxStatsSummary = (state) => {
    const stats = selectInboxStats(state);
    return {
        totalMessages: stats.total_messages || 0,
        unreadMessages: stats.unread_messages || 0,
        readMessages: stats.read_messages || 0,
        flaggedMessages: stats.flagged_messages || 0,
        escalatedMessages: stats.escalated_messages || 0,
        messagesByDay: stats.messages_by_day || {},
        topSenders: stats.top_senders || [],
        stats: stats.stats || {}
    };
};

export const selectUnreadCount = (state) => {
    const stats = selectInboxStats(state);
    return stats.unread_messages || 0;
};

export default inboxSlice.reducer;